import React, { useCallback, useContext, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Stack, Container, Typography } from '@mui/material';
import io from 'socket.io-client';

import { authContext } from '../routes';
import StyledCardNoHeight from '../components/card/StyledCardNoHeight';
import axiosInstance from '../api';
import SERVER_API_URL from '../config';
import DeleteDialog from './chat/DeleteDialog';
import { Main, Sidebar } from './chat';
import XSSidebar from './chat/XSSidebar';

export default function ChatPage() {
  const { user } = useContext(authContext);
  const [openDelete, setOpenDelete] = useState(false);
  const [openSideBar, setOpenSideBar] = useState(false);

  const [chatIdToBeDeleted, setChatIdToBeDeleted] = useState();
  const [chatIdToBeEdited, setChatIdToBeEdited] = useState();
  const [preBody, setPreBody] = useState();
  const [publicChats, setPublicChats] = useState('');
  const [socket, setSocket] = useState(null);

  const [containerWidth, setContainerWidth] = useState('xl');

  const [value, setValue] = useState('public');
  const [body, setBody] = useState('');
  const [targetType, setTargetType] = useState('public');

  const [targetId, setTargetId] = useState('public');
  const [targetName, setTargetName] = useState();

  const [districtQuery, setDistrictQuery] = useState('');
  const [districtChats, setDistrictChats] = useState('');

  const [churchQuery, setChurchQuery] = useState('');
  const [churchChats, setChurchChats] = useState('');

  const [roleQuery, setRoleQuery] = useState('');
  const [roleChats, setRoleChats] = useState('');

  const [categoryQuery, setCategoryQuery] = useState('');
  const [categoryChats, setCategoryChats] = useState('');

  const [userQuery, setUserQuery] = useState('');
  const [personalChats, setPersonalChats] = useState('');
  const [unReadChats, setUnReadChats] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      const expirationDate = new Date(decodedToken.exp * 1000);
      if (expirationDate < new Date()) {
        localStorage.clear();
        navigate('/login');
      } else if (user && !user.role.permissions.publicChat.read) {
        navigate('/dashboard');
      }
    } else {
      navigate('/login');
    }
  }, [navigate, user]);

  const getTabBadge = useCallback(async () => {
    try {
      const response = await axiosInstance.post(`${SERVER_API_URL}/chat/getUnreadChats/${user && user.id}`);
      if (response.status === 200) {
        setUnReadChats(response.data);
      }
    } catch (err) {
      // console.log(err)
    }
  }, [user]);

  const readUnreadChats = useCallback(
    async (targetType, typeId) => {
      try {
        await axiosInstance.post(`${SERVER_API_URL}/chat/readUnreadChats/${targetType}/${typeId}/${user.id}`);
      } catch (error) {
        // console.log(error);
      }
    },
    [user]
  );

  const getPublicChats = useCallback(async () => {
    try {
      const response = await axiosInstance.post(`${SERVER_API_URL}/chat/findAllPublicChats`);

      if (response.status === 200) {
        const chats = response.data;

        chats.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);

          return dateA - dateB;
        });
        setPublicChats(chats);
      }
    } catch (e) {
      // console.log(e);
    }
  }, []);

  useEffect(() => {
    const newSocket = io(`${SERVER_API_URL}`, { transports: ['websocket'] });
    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on('onMessage', (newMessage) => {
        // console.log('newMessage aaa', newMessage)
        switch (targetType) {
          case 'public':
            if (newMessage.target.public) {
              readUnreadChats('public', 'public');
              setPublicChats((prevChats) => [...prevChats, newMessage]);
            }
            break;
          case 'district':
            if (newMessage.target.districtId === targetId) {
              readUnreadChats('district', user && user.church.district.id);
              setDistrictChats((prevChats) => [...prevChats, newMessage]);
            }
            break;
          case 'church':
            if (newMessage.target.churchId === targetId) {
              readUnreadChats('church', user && user.church.id);
              setChurchChats((prevChats) => [...prevChats, newMessage]);
            }
            break;
          case 'role':
            if (newMessage.target.roleId === targetId) {
              readUnreadChats('role', user && user.role.id);
              setRoleChats((prevChats) => [...prevChats, newMessage]);
            }
            break;
          case 'category':
            if (newMessage.target.categoryId === targetId) {
              readUnreadChats('category', user && user.category.id);
              setCategoryChats((prevChats) => [...prevChats, newMessage]);
            }
            break;
          case 'personal':
            if (
              (newMessage.sender.id === user.id && newMessage.target.userId === user.id && newMessage.target.userId === targetId) ||
              (newMessage.sender.id === targetId && newMessage.target.userId === user.id) ||
              (newMessage.sender.id === user.id && newMessage.target.userId === targetId)
            ) {
              setPersonalChats((prevChats) => [...prevChats, newMessage]);
            }
            break;
          default:
            break;
        }
      });
    }

    // Clean up event listener when the component unmounts
    return () => {
      if (socket) {
        socket.off('onMessage');
      }
    };
  }, [
    socket,
    user,
    targetType,
    targetId,
    setPublicChats,
    setDistrictChats,
    setChurchChats,
    setRoleChats,
    setCategoryChats,
    readUnreadChats,
  ]);

  useEffect(() => {
    if (socket) {
      socket.on('onUpdatedMessage', (updatedMessage) => {
        switch (targetType) {
          case 'public':
            if (updatedMessage.target.public) {
              readUnreadChats('public', 'public');
              setPublicChats((prevChats) => {
                const index = prevChats.findIndex((message) => message.id === updatedMessage.id);
                if (index !== -1) {
                  const newChats = [...prevChats];
                  newChats[index] = updatedMessage;
                  return newChats;
                }
                return [...prevChats, updatedMessage];
              });
            }
            break;
          case 'district':
            if (updatedMessage.target.districtId === targetId) {
              readUnreadChats('district', user && user.church.district.id);
              setDistrictChats((prevChats) => {
                const index = prevChats.findIndex((message) => message.id === updatedMessage.id);
                if (index !== -1) {
                  const newChats = [...prevChats];
                  newChats[index] = updatedMessage;
                  return newChats;
                }
                return [...prevChats, updatedMessage];
              });
            }
            break;
          case 'church':
            if (updatedMessage.target.churchId === targetId) {
              readUnreadChats('church', user && user.church.id);
              setChurchChats((prevChats) => {
                const index = prevChats.findIndex((message) => message.id === updatedMessage.id);
                if (index !== -1) {
                  const newChats = [...prevChats];
                  newChats[index] = updatedMessage;
                  return newChats;
                }
                return [...prevChats, updatedMessage];
              });
            }
            break;
          case 'role':
            if (updatedMessage.target.roleId === targetId) {
              readUnreadChats('role', user && user.role.id);
              setRoleChats((prevChats) => {
                const index = prevChats.findIndex((message) => message.id === updatedMessage.id);
                if (index !== -1) {
                  const newChats = [...prevChats];
                  newChats[index] = updatedMessage;
                  return newChats;
                }
                return [...prevChats, updatedMessage];
              });
            }
            break;
          case 'category':
            if (updatedMessage.target.categoryId === targetId) {
              readUnreadChats('category', user && user.category.id);
              setCategoryChats((prevChats) => {
                const index = prevChats.findIndex((message) => message.id === updatedMessage.id);
                if (index !== -1) {
                  const newChats = [...prevChats];
                  newChats[index] = updatedMessage;
                  return newChats;
                }
                return [...prevChats, updatedMessage];
              });
            }
            break;
          case 'personal':
            if (
              (updatedMessage.sender.id === user.id && updatedMessage.target.userId === user.id && updatedMessage.target.userId === targetId) ||
              (updatedMessage.sender.id === targetId && updatedMessage.target.userId === user.id) ||
              (updatedMessage.sender.id === user.id && updatedMessage.target.userId === targetId)
            ) {
              setPersonalChats((prevChats) => {
                const index = prevChats.findIndex((message) => message.id === updatedMessage.id);
                if (index !== -1) {
                  const newChats = [...prevChats];
                  newChats[index] = updatedMessage;
                  return newChats;
                }
                return [...prevChats, updatedMessage];
              });
            }
            break;
          default:
            break;
        }
      });
    }

    // Clean up event listener when the component unmounts
    return () => {
      if (socket) {
        socket.off('onUpdatedMessage');
      }
    };
  }, [
    socket,
    user,
    targetType,
    targetId,
    readUnreadChats,
    setPublicChats,
    setDistrictChats,
    setChurchChats,
    setRoleChats,
    setCategoryChats,
    setPersonalChats,
  ]);

  useEffect(() => {
    if (socket) {
      socket.on('onDeletedMessage', (deletedMessage) => {
        switch (targetType) {
          case 'public':
            setPublicChats((prevChats) => prevChats.filter(message => message.id !== deletedMessage.id));
            break;
          case 'district':
            setDistrictChats((prevChats) => prevChats.filter(message => message.id !== deletedMessage.id));
            break;
          case 'church':
            setChurchChats((prevChats) => prevChats.filter(message => message.id !== deletedMessage.id));
            break;
          case 'role':
            setRoleChats((prevChats) => prevChats.filter(message => message.id !== deletedMessage.id));
            break;
          case 'category':
            setCategoryChats((prevChats) => prevChats.filter(message => message.id !== deletedMessage.id));
            break;
          case 'personal':
            setPersonalChats((prevChats) => prevChats.filter(message => message.id !== deletedMessage.id));
            break;
          default:
            break;
        }
      });
    }

    // Clean up event listener when the component unmounts
    return () => {
      if (socket) {
        socket.off('onDeletedMessage');
      }
    };
  }, [
    socket,
    targetType,
    targetId,
    user,
    setPublicChats,
    setDistrictChats,
    setChurchChats,
    setRoleChats,
    setCategoryChats,
    setPersonalChats,
  ]);


  const getDistrictChats = useCallback(
    async (selectedId) => {
      try {
        const response = await axiosInstance.post(`${SERVER_API_URL}/chat/findManyByDistrictId/${selectedId}`);

        if (response.status === 200) {
          const chats = response.data;

          chats.sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);

            return dateA - dateB;
          });
          setDistrictChats(chats);
        }
      } catch (e) {
        // console.log(e);
      }
    },
    [setDistrictChats]
  );

  const getChurchChats = useCallback(async (selectedId) => {
    try {
      const response = await axiosInstance.post(`${SERVER_API_URL}/chat/findManyByChurchId/${selectedId}`);

      if (response.status === 200) {
        const chats = response.data;

        chats.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);

          return dateA - dateB;
        });
        setChurchChats(chats);
      }
    } catch (e) {
      // console.log(e);
    }
  }, []);

  const getCategoryChats = useCallback(async (selectedId) => {
    try {
      const response = await axiosInstance.post(`${SERVER_API_URL}/chat/findManyByCategoryId/${selectedId}`);

      if (response.status === 200) {
        const chats = response.data;

        chats.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);

          return dateA - dateB;
        });
        setCategoryChats(chats);
      }
    } catch (e) {
      // console.log(e);
    }
  }, []);

  const getRoleChats = useCallback(async (selectedId) => {
    try {
      const response = await axiosInstance.post(`${SERVER_API_URL}/chat/findManyByRoleId/${selectedId}`);

      if (response.status === 200) {
        const chats = response.data;

        chats.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);

          return dateA - dateB;
        });
        setRoleChats(chats);
      }
    } catch (e) {
      // console.log(e);
    }
  }, []);

  const getPersonalChats = useCallback(
    async (selectedId) => {
      try {
        const response = await axiosInstance.post(
          `${SERVER_API_URL}/chat/findManyByUserId/${user && user.id}/${selectedId}`
        );

        if (response.status === 200) {
          const chats = response.data;

          chats.sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);

            return dateA - dateB;
          });

          setPersonalChats(chats);
        }
      } catch (e) {
        // console.log(e);
      }
    },
    [user]
  );

  const manageTarget = useCallback(
    (target) => {
      setTargetName('እርሶ');
      if (target === 'public') {
        setTargetType('public');
        setTargetId('public');
        readUnreadChats('public', 'public');
      } else if (target === 'district') {
        setTargetType('district');
        setTargetId(user && user.church.district.id);
        getDistrictChats(user && user.church.district.id);
        readUnreadChats('district', user && user.church.district.id);
      } else if (target === 'church') {
        setTargetType('church');
        setTargetId(user && user.church.id);
        getChurchChats(user && user.church.id);
        readUnreadChats('church', user && user.church.id);
      } else if (target === 'role') {
        setTargetType('role');
        setTargetId(user && user.role.id);
        getRoleChats(user && user.role.id);
        readUnreadChats('role', user && user.role.id);
      } else if (target === 'category') {
        setTargetType('category');
        setTargetId(user && user.category.id);
        getCategoryChats(user && user.category.id);
        readUnreadChats('category', user && user.category.id);
      } else if (target === 'personal') {
        setTargetType('personal');
        setTargetId(user && user.id);
        getPersonalChats(user && user.id);
        setTargetName('የግል ሳጥን');
      }
      setChatIdToBeEdited(null);
      setBody('');
      setPreBody('');
    },
    [
      user,
      setTargetType,
      setTargetId,
      getChurchChats,
      getRoleChats,
      setTargetName,
      setBody,
      setPreBody,
      getCategoryChats,
      getDistrictChats,
      getPersonalChats,
      readUnreadChats,
    ]
  );

  useEffect(() => {
    getTabBadge();
    getPublicChats();
    getDistrictChats(user && user.church.district.id);
    getChurchChats(user && user.church.id);
    getRoleChats(user && user.role.id);
    getCategoryChats(user && user.category.id);
    getPersonalChats(user && user.id);
    readUnreadChats('public', 'public');
  }, [
    user,
    getPublicChats,
    getDistrictChats,
    getChurchChats,
    getRoleChats,
    getPersonalChats,
    getCategoryChats,
    getTabBadge,
    readUnreadChats,
  ]);

  const handleSearch = (id, name) => {
    setTargetId(id);
    setTargetName(name);
    readUnreadChats(targetType, id);
    setOpenSideBar(false);

    if (targetType === 'district') {
      getDistrictChats(id);
    }

    if (targetType === 'church') {
      getChurchChats(id);
    }

    if (targetType === 'role') {
      getRoleChats(id);
    }

    if (targetType === 'category') {
      getCategoryChats(id);
    }
    
    if (targetType === 'personal') {
      getPersonalChats(id);
    }
  };

  const handleOk = async () => {
    try {
      const response = await axiosInstance.delete(`${SERVER_API_URL}/chat/deleteOneById/${chatIdToBeDeleted}`);
      if (response.status === 200) {
        setOpenDelete(false);
        getPublicChats();
        getDistrictChats(targetId);
        getChurchChats(targetId);
        getCategoryChats(targetId);
        getRoleChats(targetId);
        getPersonalChats(targetId);
        setChatIdToBeDeleted(null);
      }
    } catch (error) {
      // console.log(error)
    }
  };

  const handleDelete = async (chatId) => {
    setChatIdToBeDeleted(chatId);
    setOpenDelete(true);
  };

  const handleEdit = (chatId, body) => {
    setChatIdToBeEdited(chatId);
    setBody(body);
    setPreBody(body);
  };

  const cancelEdit = () => {
    setChatIdToBeEdited(null);
    setBody('');
    setPreBody('');
  };

  const postNewChat = useCallback(async () => {
    try {
      if (chatIdToBeEdited) {
        const response = await axiosInstance.put(`${SERVER_API_URL}/chat/updateOne/${chatIdToBeEdited}`, {
          body,
        });

        if (response.status === 200) {
          setChatIdToBeEdited(null);
          setBody('');
          setPreBody('');
        }
      } else {
        const response = await axiosInstance.post(`${SERVER_API_URL}/chat/registerOne/${user && user.id}`, {
          body,
          targetType,
          targetId,
        });

        if (response.status === 201) {
          setBody('');
        }
      }
    } catch (e) {
      // console.log(e);
    }

  }, [chatIdToBeEdited, user, body, targetType, targetId]);

  return (
    <>
      <Helmet>
        <title> ውይይት | የሰ/ሸ/ሃ/መ/አ/ስርዓት </title>
      </Helmet>

      <Container maxWidth={containerWidth}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ display: { xs: 'none', sm: 'block' }, mb: { xs: 0, sm: 2 } }}
        >
          <Typography variant="h4" gutterBottom color="text.secondary">
            ውይይት
          </Typography>
        </Stack>
        <StyledCardNoHeight sx={{ position: 'relative', padding: { xs: 0.2, sm: 2 }, display: 'flex' }}>
          <Sidebar
            user={user}
            targetType={targetType}
            targetId={targetId}
            roleQuery={roleQuery}
            handleSearch={handleSearch}
            categoryQuery={categoryQuery}
            churchQuery={churchQuery}
            districtQuery={districtQuery}
            setRoleQuery={setRoleQuery}
            setCategoryQuery={setCategoryQuery}
            setChurchQuery={setChurchQuery}
            setDistrictQuery={setDistrictQuery}
            containerWidth={containerWidth}
            setContainerWidth={setContainerWidth}
            userQuery={userQuery}
            setUserQuery={setUserQuery}
          />
          <XSSidebar
            user={user}
            targetType={targetType}
            targetId={targetId}
            roleQuery={roleQuery}
            handleSearch={handleSearch}
            categoryQuery={categoryQuery}
            churchQuery={churchQuery}
            districtQuery={districtQuery}
            setRoleQuery={setRoleQuery}
            setCategoryQuery={setCategoryQuery}
            setChurchQuery={setChurchQuery}
            setDistrictQuery={setDistrictQuery}
            containerWidth={containerWidth}
            userQuery={userQuery}
            setUserQuery={setUserQuery}
            openSideBar={openSideBar}
            setOpenSideBar={setOpenSideBar}
          />
          <Main
            user={user}
            targetType={targetType}
            targetName={targetName}
            churchChats={churchChats}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
            roleChats={roleChats}
            categoryChats={categoryChats}
            publicChats={publicChats}
            personalChats={personalChats}
            districtChats={districtChats}
            unReadChats={unReadChats}
            manageTarget={manageTarget}
            value={value}
            setValue={setValue}
            body={body}
            preBody={preBody}
            setBody={setBody}
            postNewChat={postNewChat}
            cancelEdit={cancelEdit}
            chatIdToBeEdited={chatIdToBeEdited}
            openSideBar={openSideBar}
            setOpenSideBar={setOpenSideBar}
            targetId={targetId}
          />
        </StyledCardNoHeight>
      </Container>
      <DeleteDialog openDelete={openDelete} setOpenDelete={setOpenDelete} handleOk={handleOk} />
    </>
  );
}
